// src/components/dashboard/companyadmin/CompanyDashboardView.js
// The company admin's cards+charts+department-drilldown, extracted so
// Master Admin's company drilldown can reuse the exact same fetch logic
// and rendering (including its own nested department drilldown) by
// passing a companyIdOverride instead of relying on the viewer's own
// session.
import React, { useEffect, useState } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import axios from 'axios';
import StatTile from '../departmentadmin/StatTile';
import MessageBoardWidget from '../MessageBoardWidget';
import ParticipantTrendChart from '../departmentadmin/ParticipantTrendChart';
import TrainingHoursChart from '../departmentadmin/TrainingHoursChart';
import DepartmentDashboardView from '../departmentadmin/DepartmentDashboardView';
import DepartmentComparisonChart from './DepartmentComparisonChart';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function CompanyDashboardView({ companyIdOverride }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState(null); // { id, name }

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setSelectedDepartment(null); // reset any drilldown if the company changes

    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/stat/get_company_dashboard_stats.php`, {
          params: companyIdOverride ? { company_id: companyIdOverride } : {},
          withCredentials: true
        });
        if (isMounted) setStats(response.data);
      } catch (error) {
        console.error('Error fetching company dashboard stats:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchStats();
    return () => { isMounted = false; };
  }, [companyIdOverride]);

  if (selectedDepartment) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{selectedDepartment.name}</h5>
          <Button variant="outline-secondary" size="sm" onClick={() => setSelectedDepartment(null)}>
            &larr; Back to Company Overview
          </Button>
        </div>
        <DepartmentDashboardView departmentIdOverride={selectedDepartment.id} />
      </div>
    );
  }

  return (
    <>
      <Row className="g-3 mb-1">
        <Col md={3}>
          <StatTile title="Upcoming Trainings" icon="bi-calendar-event" value={stats?.upcomingTrainingsCount ?? 0} loading={loading} />
        </Col>
        <Col md={3}>
          <StatTile title="Employees Assigned" icon="bi-people" value={stats?.employeesAssignedCount ?? 0} loading={loading} />
        </Col>
        <Col md={3}>
          <StatTile title="Pending Requests" icon="bi-hourglass-split" value={stats?.pendingRequestsCount ?? 0} loading={loading} />
        </Col>
        <Col md={3}>
          <StatTile title="Certificates This Month" icon="bi-award" value={stats?.certificatesThisMonthCount ?? 0} loading={loading} />
        </Col>
        {/* Messages are personal to the viewing session, not company-scoped —
            only shown on the company admin's own dashboard, not when a
            master admin is drilling into this company's view. */}
        {!companyIdOverride && (
          <Col md={3}>
            <MessageBoardWidget />
          </Col>
        )}
      </Row>
      <Row className="g-3 mt-1">
        <Col md={6}>
          <ParticipantTrendChart data={stats?.participantTrend ?? []} loading={loading} />
        </Col>
        <Col md={6}>
          <TrainingHoursChart data={stats?.trainingHours ?? []} loading={loading} />
        </Col>
      </Row>
      <Row className="g-3 mt-1">
        <Col md={12}>
          <DepartmentComparisonChart
            data={stats?.departmentComparison ?? []}
            loading={loading}
            onSelectDepartment={(dept) => setSelectedDepartment(dept)}
          />
        </Col>
      </Row>
    </>
  );
}

export default CompanyDashboardView;
